import type { CatalogCloneReview, CatalogTask, Role } from '@/api'
import { refusalCode } from '@/api'

/**
 * The pieces of "Add a teammate" (#1758) that are rules rather than screen: how a described job's role is matched or
 * named, which role the company already holds for a Catalog job, and what each tool the kept tasks need is needed for.
 */

/** The most a role slug may hold, as the API takes it. */
const SLUG_MAX = 64

/**
 * The slug a role made from a described job is created with: the name in lower case, every run of anything but a
 * letter or digit one hyphen, with none at either end. "Social Media Manager" is `social-media-manager`.
 */
export function slugFromName(name: string): string {
    return name
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .slice(0, SLUG_MAX)
        .replace(/^-+|-+$/g, '')
}

/** A name as two names are compared: trimmed, inner spaces as one, and case ignored. */
function nameKey(name: string | null | undefined): string {
    return (name ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
}

/**
 * The company's role whose name is the described job's title (FT2, CTO decision 5): the same name, whatever its case
 * or surrounding spaces, or undefined when none is. Never a near match: a fuzzy one could give the wrong job.
 */
export function roleNamed(roles: readonly Role[], title: string): Role | undefined {
    const key = nameKey(title)
    return key ? roles.find(role => nameKey(role.name) === key) : undefined
}

/**
 * The company's own copy of a Catalog role, for when the clone is refused as already cloned (CTO decision 4): the role
 * with the template's slug, else the one with its name, else undefined.
 */
export function heldCopyOf(roles: readonly Role[], template: { slug: string; name: string }): Role | undefined {
    return roles.find(role => role.slug === template.slug) ?? roleNamed(roles, template.name)
}

/** One tool the kept tasks need that the company has not connected, with the kept tasks that use it (its reason). */
export type ToolNeed = { tool: string; tasks: CatalogTask[] }

/**
 * Every tool the kept tasks need that the company has not connected, from the role review, in the order the tasks
 * first name them. A tool only an unticked task needed is not listed, and an unticked task is no tool's reason.
 */
export function toolNeeds(
    tasks: readonly CatalogTask[],
    keptIds: readonly string[],
    review: CatalogCloneReview['tasks'],
): ToolNeed[] {
    const needs = new Map<string, CatalogTask[]>()
    for (const task of tasks) {
        if (!keptIds.includes(task.id)) continue
        const missing = review.find(row => row.taskId === task.id)?.missing ?? []
        for (const tool of missing) {
            needs.set(tool, [...(needs.get(tool) ?? []), task])
        }
    }
    return [...needs.entries()].map(([tool, users]) => ({ tool, tasks: users }))
}

/**
 * What a refusal is known by: its code and its message, either of which may carry the API's code (the Catalog clone
 * panel reads the message). Empty for anything that is not an Error.
 */
export function refusalWords(error: unknown): string[] {
    if (!(error instanceof Error)) return []
    const code = refusalCode(error)
    return code ? [code, error.message] : [error.message]
}
